import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface DiagnosticCategory {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface SpecimenType {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface DiagnosticDepartment {
  id: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
  isDiagnostic?: boolean;
  showOnWebsite?: boolean;
  rooms?: DiagnosticRoom[];
}

export interface Floor {
  id: string;
  name: string;
  description?: string;
  floorNumber: number;
  createdAt: string;
  updatedAt: string;
}

export interface DiagnosticRoom {
  id: string;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE';
  floorId?: string;
  floor?: Floor;
}

export interface DiagnosticAccessory {
  id: string;
  code: string;
  name: string;
  price: number;
  isContainer: boolean;
  specimenTypeId?: string | null;
  specimenType?: SpecimenType;
  color?: string | null;
  defaultVolume?: string | null;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface DiagnosticTestAccessory {
  id: string;
  diagnosticTestId: string;
  diagnosticAccessoryId: string;
  accessory?: DiagnosticAccessory;
  defaultQuantity: number;
  isSpecimenContainer: boolean;
  requiredVolume?: string | null;
}

export interface DiagnosticTest {
  id: string;
  code: string;
  name: string;
  category: 'LAB' | 'RADIOLOGY' | 'CARDIOLOGY' | 'OTHER';
  categoryId?: string;
  diagnosticCategory?: DiagnosticCategory;
  diagnosticDepartmentId: string;
  department?: DiagnosticDepartment;
  price: number;
  discountPercent?: number;
  turnaroundTime?: number;
  preparationInstructions?: string;
  roomNumber?: string;
  diagnosticRoomId?: string;
  room?: DiagnosticRoom;
  status: 'ACTIVE' | 'INACTIVE';
  accessories?: DiagnosticTestAccessory[];
  serviceCatalogId?: string;
}

export interface DiagnosticReceiptItem {
  id: string;
  receiptId: string;
  diagnosticTestId?: string;
  test?: DiagnosticTest;
  testName?: string;
  diagnosticAccessoryId?: string;
  accessory?: DiagnosticAccessory;
  accessoryName?: string;
  diagnosticDepartmentId: string;
  department?: DiagnosticDepartment;
  roomNumber?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  deliveryAt?: string;
  status: 'PENDING' | 'COMPLETED' | 'REFUNDED';
  isAccessory: boolean;
  receipt?: DiagnosticReceipt;
}

export interface DiagnosticReceipt {
  id: string;
  receiptNumber: string;
  patientId: string;
  patient?: { id: string; firstName: string; lastName: string; mobile: string; age?: number; gender?: string; mobileNumber?: string };
  visitId?: string;
  totalAmount: number;
  discountPercent?: number;
  discountAmount?: number;
  extraDiscountAmount?: number;
  extraDiscountType?: 'FLAT' | 'PERCENT';
  extraDiscountValue?: number;
  paidAmount?: number;
  dueAmount?: number;
  /** Promised payment date for the outstanding balance (tracking only). */
  dueDate?: string | null;
  /** When the receipt was fully settled (actual last payment date). */
  paidAt?: string | null;
  payments?: Array<{
    id: string;
    amount: number;
    paymentMode: string;
    transactionReference?: string;
    paidAt: string;
    createdById?: string;
  }>;
  paymentMode: string;
  transactionReference?: string;
  status: 'ACTIVE' | 'USED' | 'PARTIALLY_USED' | 'REFUNDED';
  items?: DiagnosticReceiptItem[];
  specimens?: LabSpecimen[];
  createdById?: string;
  createdBy?: { id: string; firstName?: string; lastName?: string; employee?: { signatureUrl?: string } };
  referringDoctorId?: string;
  referringDoctorName?: string;
  referringDoctor?: { id: string; name: string; qualifications?: string[] };
  /** Reference-only doctor (informational; never part of revenue share). */
  doctorReferenceId?: string;
  doctorReferenceName?: string;
  doctorReference?: { id: string; name?: string };
  referralId?: string;
  /** Effective referral share % for THIS receipt (overrides the partner default). */
  referralSharePercent?: number | null;
  /** Effective referral share % for the separate referring doctor on THIS receipt. */
  referral?: {
    id: string;
    name: string;
    type: 'DOCTOR' | 'EMPLOYEE' | 'OTHER';
    defaultSharePercent?: number;
    designation?: string;
    specialization?: string;
    organization?: string;
  };
  doctorRecommendation?: string;
  createdAt: string;
}

export interface DiagnosticRouting {
  id: string;
  diagnosticReceiptId: string;
  receipt?: DiagnosticReceipt;
  diagnosticDepartmentId: string;
  department?: DiagnosticDepartment;
  roomNumber?: string;
  status: 'PENDING' | 'WAITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  departmentNotes?: string;
  updatedById?: string;
  createdAt: string;
}

export interface LabSpecimenItem {
  id: string;
  specimenId: string;
  diagnosticReceiptItemId: string;
  receiptItem?: DiagnosticReceiptItem;
  labOrderId?: string;
}

export interface LabSpecimen {
  id: string;
  specimenBarcode: string;
  receiptId: string;
  receipt?: { id: string; receiptNumber?: string };
  patientId: string;
  patient?: { id: string; firstName: string; lastName: string; mobile?: string; mobileNumber?: string; age?: number; gender?: string };
  visitId?: string;
  departmentId: string;
  department?: DiagnosticDepartment;
  roomId?: string;
  room?: DiagnosticRoom;
  specimenTypeId?: string;
  specimenType?: SpecimenType;
  containerAccessoryId?: string;
  containerAccessory?: DiagnosticAccessory;
  specimenTypeCode?: string;
  specimenContainerCode?: string;
  containerColor?: string;
  status: 'PENDING' | 'COLLECTED' | 'RECEIVED' | 'PROCESSING' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
  isUrgent: boolean;
  collectedAt?: string;
  receivedAt?: string;
  completedAt?: string;
  rejectionReason?: string;
  rejectedAt?: string;
  redrawOfId?: string;
  labelPrintedAt?: string;
  printCount: number;
  items?: LabSpecimenItem[];
  createdAt: string;
}

/**
 * Fetch the diagnostic receipt PDF as an object URL. Shared by the print
 * (open in a new tab) and download (save to disk) flows.
 */
async function fetchReceiptPdfUrl(id: string): Promise<string> {
  const response = await httpService.get(`/billing/diagnostic-receipts/${id}/print`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

export const diagnosticService = {
  // Categories
  getCategories: (page = 1, limit = 20, search?: string): Promise<ApiResponse<DiagnosticCategory[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticCategory[]>(`/diagnostic/categories?${params.toString()}`) as Promise<ApiResponse<DiagnosticCategory[]> & { meta: PaginationMeta }>;
  },

  getActiveCategories: (): Promise<ApiResponse<DiagnosticCategory[]>> => {
    return get<DiagnosticCategory[]>('/diagnostic/categories/active/all');
  },

  getCategory: (id: string): Promise<ApiResponse<DiagnosticCategory>> => {
    return get<DiagnosticCategory>(`/diagnostic/categories/${id}`);
  },

  createCategory: (data: any): Promise<ApiResponse<DiagnosticCategory>> => {
    return post<DiagnosticCategory>('/diagnostic/categories', data);
  },

  updateCategory: (id: string, data: any): Promise<ApiResponse<DiagnosticCategory>> => {
    return patch<DiagnosticCategory>(`/diagnostic/categories/${id}`, data);
  },

  deleteCategory: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/diagnostic/categories/${id}`);
  },

  // Specimen Types (dynamic sample type catalogue)
  getSpecimenTypes: (page = 1, limit = 20, search?: string): Promise<ApiResponse<SpecimenType[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<SpecimenType[]>(`/diagnostic/specimen-types?${params.toString()}`) as Promise<ApiResponse<SpecimenType[]> & { meta: PaginationMeta }>;
  },

  getActiveSpecimenTypes: (): Promise<ApiResponse<SpecimenType[]>> => {
    return get<SpecimenType[]>('/diagnostic/specimen-types/active/all');
  },

  getSpecimenType: (id: string): Promise<ApiResponse<SpecimenType>> => {
    return get<SpecimenType>(`/diagnostic/specimen-types/${id}`);
  },

  createSpecimenType: (data: any): Promise<ApiResponse<SpecimenType>> => {
    return post<SpecimenType>('/diagnostic/specimen-types', data);
  },

  updateSpecimenType: (id: string, data: any): Promise<ApiResponse<SpecimenType>> => {
    return patch<SpecimenType>(`/diagnostic/specimen-types/${id}`, data);
  },

  deleteSpecimenType: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/diagnostic/specimen-types/${id}`);
  },

  // Departments
  getDepartments: (page = 1, limit = 20, search?: string, all = false): Promise<ApiResponse<DiagnosticDepartment[]> & { meta: PaginationMeta; stats?: { trashed?: number } }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (all) params.set('all', 'true');
    return get<DiagnosticDepartment[]>(`/diagnostic/departments?${params.toString()}`) as Promise<ApiResponse<DiagnosticDepartment[]> & { meta: PaginationMeta; stats?: { trashed?: number } }>;
  },

  getDepartment: (id: string): Promise<ApiResponse<DiagnosticDepartment>> => {
    return get<DiagnosticDepartment>(`/diagnostic/departments/${id}`);
  },

  createDepartment: (data: any): Promise<ApiResponse<DiagnosticDepartment>> => {
    return post<DiagnosticDepartment>('/diagnostic/departments', data);
  },

  updateDepartment: (id: string, data: any): Promise<ApiResponse<DiagnosticDepartment>> => {
    return patch<DiagnosticDepartment>(`/diagnostic/departments/${id}`, data);
  },

  deleteDepartment: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/diagnostic/departments/${id}`);
  },

  /** List soft-deleted departments (the Trash page). */
  getTrashedDepartments: (
    page = 1,
    limit = 20,
    search?: string
  ): Promise<ApiResponse<DiagnosticDepartment[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticDepartment[]>(`/diagnostic/departments/trash?${params.toString()}`) as Promise<ApiResponse<DiagnosticDepartment[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed departments back to the active list. */
  restoreDepartments: (
    ids: string[]
  ): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/departments/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed departments. */
  permanentlyDeleteDepartments: (
    ids: string[]
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/departments/trash/bulk-delete', { ids });
  },

  // Rooms
  getRooms: (page = 1, limit = 20, search?: string): Promise<ApiResponse<DiagnosticRoom[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticRoom[]>(`/diagnostic/rooms?${params.toString()}`) as Promise<ApiResponse<DiagnosticRoom[]> & { meta: PaginationMeta }>;
  },

  getRoom: (id: string): Promise<ApiResponse<DiagnosticRoom>> => {
    return get<DiagnosticRoom>(`/diagnostic/rooms/${id}`);
  },

  createRoom: (data: any): Promise<ApiResponse<DiagnosticRoom>> => {
    return post<DiagnosticRoom>('/diagnostic/rooms', data);
  },

  updateRoom: (id: string, data: any): Promise<ApiResponse<DiagnosticRoom>> => {
    return patch<DiagnosticRoom>(`/diagnostic/rooms/${id}`, data);
  },

  deleteRoom: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/diagnostic/rooms/${id}`);
  },

  // Floors
  getFloors: (page = 1, limit = 20, search?: string): Promise<ApiResponse<Floor[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<Floor[]>(`/floors?${params.toString()}`) as Promise<ApiResponse<Floor[]> & { meta: PaginationMeta }>;
  },

  getAllFloors: (): Promise<ApiResponse<Floor[]>> => {
    return get<Floor[]>('/floors?limit=1000');
  },

  getFloor: (id: string): Promise<ApiResponse<Floor>> => {
    return get<Floor>(`/floors/${id}`);
  },

  createFloor: (data: any): Promise<ApiResponse<Floor>> => {
    return post<Floor>('/floors', data);
  },

  updateFloor: (id: string, data: any): Promise<ApiResponse<Floor>> => {
    return patch<Floor>(`/floors/${id}`, data);
  },

  deleteFloor: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/floors/${id}`);
  },

  // Tests
  getTests: (
    page = 1,
    limit = 20,
    search?: string,
    sortBy?: string,
    sortOrder?: 'ASC' | 'DESC',
    filters?: { categoryId?: string; status?: 'ACTIVE' | 'INACTIVE' },
  ): Promise<ApiResponse<DiagnosticTest[]> & { meta: PaginationMeta; stats?: { trashed?: number } }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    if (filters?.categoryId) params.set('categoryId', filters.categoryId);
    if (filters?.status) params.set('status', filters.status);
    return get<DiagnosticTest[]>(`/diagnostic/tests?${params.toString()}`) as Promise<ApiResponse<DiagnosticTest[]> & { meta: PaginationMeta; stats?: { trashed?: number } }>;
  },

  getActiveTests: (): Promise<ApiResponse<DiagnosticTest[]>> => {
    return get<DiagnosticTest[]>('/diagnostic/tests/active/all');
  },

  getTestsByDepartment: (departmentId: string): Promise<ApiResponse<DiagnosticTest[]>> => {
    return get<DiagnosticTest[]>(`/diagnostic/tests/department/${departmentId}`);
  },

  getTest: (id: string): Promise<ApiResponse<DiagnosticTest>> => {
    return get<DiagnosticTest>(`/diagnostic/tests/${id}`);
  },

  createTest: (data: any): Promise<ApiResponse<DiagnosticTest>> => {
    return post<DiagnosticTest>('/diagnostic/tests', data);
  },

  updateTest: (id: string, data: any): Promise<ApiResponse<DiagnosticTest>> => {
    return patch<DiagnosticTest>(`/diagnostic/tests/${id}`, data);
  },

  deleteTest: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/diagnostic/tests/${id}`);
  },

  /** List soft-deleted tests (the Trash page). */
  getTrashedTests: (
    page = 1,
    limit = 20,
    search?: string
  ): Promise<ApiResponse<DiagnosticTest[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticTest[]>(`/diagnostic/tests/trash?${params.toString()}`) as Promise<ApiResponse<DiagnosticTest[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed tests back to the active list. */
  restoreTests: (
    ids: string[]
  ): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/tests/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed tests. */
  permanentlyDeleteTests: (
    ids: string[]
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/tests/trash/bulk-delete', { ids });
  },

  // Accessories
  getAccessories: (page = 1, limit = 20, search?: string): Promise<ApiResponse<DiagnosticAccessory[]> & { meta: PaginationMeta; stats?: { trashed?: number } }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticAccessory[]>(`/diagnostic/accessories?${params.toString()}`) as Promise<ApiResponse<DiagnosticAccessory[]> & { meta: PaginationMeta; stats?: { trashed?: number } }>;
  },

  getActiveAccessories: (): Promise<ApiResponse<DiagnosticAccessory[]>> => {
    return get<DiagnosticAccessory[]>('/diagnostic/accessories/active/all');
  },

  getAccessory: (id: string): Promise<ApiResponse<DiagnosticAccessory>> => {
    return get<DiagnosticAccessory>(`/diagnostic/accessories/${id}`);
  },

  createAccessory: (data: any): Promise<ApiResponse<DiagnosticAccessory>> => {
    return post<DiagnosticAccessory>('/diagnostic/accessories', data);
  },

  updateAccessory: (id: string, data: any): Promise<ApiResponse<DiagnosticAccessory>> => {
    return patch<DiagnosticAccessory>(`/diagnostic/accessories/${id}`, data);
  },

  deleteAccessory: (id: string): Promise<ApiResponse<void>> => {
    return httpService.delete(`/diagnostic/accessories/${id}`).then((res) => res.data);
  },

  /** List soft-deleted accessories (the Trash page). */
  getTrashedAccessories: (
    page = 1,
    limit = 20,
    search?: string
  ): Promise<ApiResponse<DiagnosticAccessory[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<DiagnosticAccessory[]>(`/diagnostic/accessories/trash?${params.toString()}`) as Promise<ApiResponse<DiagnosticAccessory[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed accessories back to the active list. */
  restoreAccessories: (
    ids: string[]
  ): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/accessories/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed accessories. */
  permanentlyDeleteAccessories: (
    ids: string[]
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/diagnostic/accessories/trash/bulk-delete', { ids });
  },

  // Receipts
  getReceipts: (page = 1, limit = 20, filters?: { search?: string; status?: string; paymentMode?: string; paymentStatus?: string; isReferral?: 'true' | 'false'; dateFrom?: string; dateTo?: string; dateBasis?: 'document' | 'settled'; departmentId?: string; patientId?: string; patientMobile?: string }): Promise<ApiResponse<DiagnosticReceipt[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.paymentMode) params.set('paymentMode', filters.paymentMode);
    if (filters?.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
    if (filters?.isReferral) params.set('isReferral', filters.isReferral);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.dateBasis) params.set('dateBasis', filters.dateBasis);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.patientId) params.set('patientId', filters.patientId);
    if (filters?.patientMobile) params.set('patientMobile', filters.patientMobile);
    return get<DiagnosticReceipt[]>(`/billing/diagnostic-receipts?${params.toString()}`) as Promise<ApiResponse<DiagnosticReceipt[]> & { meta: PaginationMeta }>;
  },

  /** Receipt totals (billed / paid / due) for one patient. */
  getPatientReceiptSummary: (
    patientId: string,
  ): Promise<ApiResponse<{ count: number; billed: number; paid: number; due: number }>> => {
    return get<{ count: number; billed: number; paid: number; due: number }>(
      `/billing/diagnostic-receipts/patients/${patientId}/summary`,
    );
  },

  getReceipt: (id: string): Promise<ApiResponse<DiagnosticReceipt>> => {
    return get<DiagnosticReceipt>(`/billing/diagnostic-receipts/${id}`);
  },

  createReceipt: (data: any): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>('/billing/diagnostic-receipts', data);
  },

  updateReceipt: (id: string, data: any): Promise<ApiResponse<DiagnosticReceipt>> => {
    return patch<DiagnosticReceipt>(`/billing/diagnostic-receipts/${id}`, data);
  },

  /**
   * Collect an outstanding balance against a receipt. The payment is dated on
   * the day it is received, so it lands in the ledger/cashbook on that day
   * rather than the receipt's creation date.
   */
  recordReceiptPayment: (
    id: string,
    data: {
      amount: number;
      paymentMode: string;
      transactionReference?: string;
      paymentDate?: string;
      dueDate?: string;
      extraDiscountType?: 'FLAT' | 'PERCENT';
      extraDiscountValue?: number;
    },
  ): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>(`/billing/diagnostic-receipts/${id}/payments`, data);
  },

  deleteReceipt: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/billing/diagnostic-receipts/${id}`);
  },

  /**
   * Fold other unpaid receipts of the same patient into one receipt. Their
   * tests, accessory lines and collected payments move to the target; the
   * source receipts are moved to the trash.
   */
  mergeReceipts: (
    targetReceiptId: string,
    sourceReceiptIds: string[],
  ): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>(`/billing/diagnostic-receipts/${targetReceiptId}/merge`, {
      sourceReceiptIds,
    });
  },

  getTrashedReceipts: (
    page = 1,
    limit = 20,
    filters?: { search?: string; status?: string; paymentMode?: string; paymentStatus?: string; dateFrom?: string; dateTo?: string; departmentId?: string },
  ): Promise<ApiResponse<DiagnosticReceipt[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.paymentMode) params.set('paymentMode', filters.paymentMode);
    if (filters?.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    return get<DiagnosticReceipt[]>(`/billing/diagnostic-receipts/trash?${params.toString()}`) as Promise<
      ApiResponse<DiagnosticReceipt[]> & { meta: PaginationMeta }
    >;
  },

  trashReceipts: (
    ids: string[],
  ): Promise<ApiResponse<{ trashed: string[]; notFound: string[]; blocked: { id: string; reason: string }[] }>> => {
    return post<{ trashed: string[]; notFound: string[]; blocked: { id: string; reason: string }[] }>(
      '/billing/diagnostic-receipts/bulk-trash',
      { ids },
    );
  },

  restoreReceipts: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/diagnostic-receipts/trash/bulk-restore', { ids });
  },

  permanentDeleteReceipts: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/diagnostic-receipts/trash/bulk-delete', { ids });
  },

  printReceipt: (id: string): Promise<string> => fetchReceiptPdfUrl(id),

  /** Fetch the receipt PDF as an object URL so the browser can save it to disk. */
  downloadReceipt: (id: string): Promise<string> => fetchReceiptPdfUrl(id),

  verifyReceipt: (receiptNumber: string): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>('/billing/diagnostic-receipts/verify', { receiptNumber });
  },

  scanBarcode: (barcode: string): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>('/billing/diagnostic-receipts/scan-barcode', { barcode });
  },

  getTestBarcodesPdf: async (receiptId: string): Promise<string> => {
    const response = await httpService.get(`/billing/diagnostic-receipts/${receiptId}/test-barcodes`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  // Specimens (physical collection containers)
  getReceiptSpecimens: (receiptId: string): Promise<ApiResponse<LabSpecimen[]>> => {
    return get<LabSpecimen[]>(`/billing/diagnostic-receipts/${receiptId}/specimens`);
  },

  planReceiptSpecimens: (receiptId: string): Promise<ApiResponse<LabSpecimen[]>> => {
    return post<LabSpecimen[]>(`/billing/diagnostic-receipts/${receiptId}/specimens/plan`, {});
  },

  getSpecimenLabelsPdf: async (specimenIds: string[]): Promise<string> => {
    const response = await httpService.post(
      `/billing/diagnostic-receipts/specimens/labels`,
      { specimenIds },
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  /**
   * Print labels straight to the workstation's configured label printer. The
   * printer share/host are sent along so each machine can target its own
   * printer. No browser print dialog is shown.
   */
  printSpecimenLabels: (
    specimenIds: string[],
    shareName?: string,
    host?: string,
  ): Promise<ApiResponse<{ printed: number; delivered: boolean }>> => {
    return post<{ printed: number; delivered: boolean }>(`/billing/diagnostic-receipts/specimens/labels/print`, {
      specimenIds,
      shareName,
      host,
    });
  },

  /**
   * Render labels as base64 ESC/POS without printing. Used with the local print
   * agent: the cloud backend builds the bytes, the workstation prints them.
   */
  renderSpecimenLabels: (
    specimenIds: string[],
    shareName?: string,
    host?: string,
  ): Promise<ApiResponse<{ base64: string; shareName: string; host: string; count: number }>> => {
    return post<{ base64: string; shareName: string; host: string; count: number }>(
      `/billing/diagnostic-receipts/specimens/labels/render`,
      { specimenIds, shareName, host },
    );
  },

  /**
   * Mark labels as printed after the local print agent confirms the job, so the
   * metadata/audit trail matches the server-side print path.
   */
  confirmSpecimenLabelsPrinted: (
    specimenIds: string[],
  ): Promise<ApiResponse<{ printed: number }>> => {
    return post<{ printed: number }>(
      `/billing/diagnostic-receipts/specimens/labels/confirm`,
      { specimenIds },
    );
  },

  // Specimen lifecycle (collection station → lab → bench)
  getSpecimens: (filters?: {
    status?: string;
    departmentId?: string;
    categoryId?: string;
    roomId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<LabSpecimen[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.categoryId) params.set('categoryId', filters.categoryId);
    if (filters?.roomId) params.set('roomId', filters.roomId);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return get<LabSpecimen[]>(`/diagnostic/specimens${qs ? `?${qs}` : ''}`) as Promise<ApiResponse<LabSpecimen[]> & { meta: PaginationMeta }>;
  },

  getSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return get<LabSpecimen>(`/diagnostic/specimens/${id}`);
  },

  scanSpecimen: (barcode: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>('/diagnostic/specimens/scan', { barcode });
  },

  collectSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/collect`, {});
  },

  receiveSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/receive`, {});
  },

  processSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/process`, {});
  },

  completeSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/complete`, {});
  },

  rejectSpecimen: (id: string, reason: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/reject`, { reason });
  },

  redrawSpecimen: (id: string): Promise<ApiResponse<LabSpecimen>> => {
    return post<LabSpecimen>(`/diagnostic/specimens/${id}/redraw`, {});
  },

  deleteSpecimen: (
    id: string,
  ): Promise<ApiResponse<{ id: string; receiptId: string; specimenBarcode: string }>> => {
    return del<{ id: string; receiptId: string; specimenBarcode: string }>(`/diagnostic/specimens/${id}`);
  },

  syncSpecimensForOrder: (orderId: string): Promise<ApiResponse<{ synced: boolean }>> => {
    return post<{ synced: boolean }>(`/diagnostic/specimens/sync-order/${orderId}`, {});
  },

  refundReceipt: (id: string, reason: string, approvedById?: string): Promise<ApiResponse<DiagnosticReceipt>> => {
    return post<DiagnosticReceipt>(`/billing/diagnostic-receipts/${id}/refund`, { reason, approvedById });
  },

  // Dashboard
  getDepartmentDashboard: (departmentId: string): Promise<ApiResponse<DiagnosticRouting[]>> => {
    return get<DiagnosticRouting[]>(`/billing/diagnostic-receipts/dashboard/${departmentId}`);
  },

  getRouting: (routingId: string): Promise<ApiResponse<DiagnosticRouting>> => {
    return get<DiagnosticRouting>(`/billing/diagnostic-receipts/routing/${routingId}`);
  },

  getAllRoutings: (filters?: { status?: string; departmentId?: string; categoryId?: string; dateFrom?: string; dateTo?: string; search?: string }): Promise<ApiResponse<DiagnosticRouting[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.categoryId) params.set('categoryId', filters.categoryId);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.search) params.set('search', filters.search);
    return get<DiagnosticRouting[]>(`/billing/diagnostic-receipts/routings?${params.toString()}`);
  },

  getRoutingsPaginated: (
    page = 1,
    limit = 20,
    filters?: { status?: string; departmentId?: string; categoryId?: string; dateFrom?: string; dateTo?: string; search?: string },
  ): Promise<ApiResponse<DiagnosticRouting[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return get<DiagnosticRouting[]>(`/billing/diagnostic-receipts/routings?${params.toString()}`) as Promise<
      ApiResponse<DiagnosticRouting[]> & { meta: PaginationMeta }
    >;
  },

  getTrashedRoutings: (
    page = 1,
    limit = 20,
    filters?: { search?: string; departmentId?: string; dateFrom?: string; dateTo?: string },
  ): Promise<ApiResponse<DiagnosticRouting[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return get<DiagnosticRouting[]>(`/billing/diagnostic-receipts/routings/trash?${params.toString()}`) as Promise<
      ApiResponse<DiagnosticRouting[]> & { meta: PaginationMeta }
    >;
  },

  trashRoutings: (ids: string[]): Promise<ApiResponse<{ trashed: string[]; notFound: string[] }>> => {
    return post<{ trashed: string[]; notFound: string[] }>('/billing/diagnostic-receipts/routings/bulk-trash', { ids });
  },

  restoreRoutings: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/diagnostic-receipts/routings/trash/bulk-restore', { ids });
  },

  permanentDeleteRoutings: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/diagnostic-receipts/routings/trash/bulk-delete', { ids });
  },

  updateRoutingStatus: (routingId: string, data: any): Promise<ApiResponse<DiagnosticRouting>> => {
    return patch<DiagnosticRouting>(`/billing/diagnostic-receipts/dashboard/${routingId}/status`, data);
  },

  // Revenue Distributions
  getRevenueDistributions: (params?: {
    status?: string;
    sourceType?: string;
    departmentId?: string;
    doctorId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.sourceType) qs.set('sourceType', params.sourceType);
    if (params?.departmentId) qs.set('departmentId', params.departmentId);
    if (params?.doctorId) qs.set('doctorId', params.doctorId);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<RevenueDistribution[]>(`/billing/revenue-distributions?${qs.toString()}`) as Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }>;
  },

  getRevenueDistributionSummary: (params?: {
    status?: string;
    doctorId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<
    ApiResponse<RevenueDistributionSummary[]> & {
      meta: PaginationMeta;
      stats?: {
        totals: {
          totalAmount: number;
          doctorShare: number;
          hospitalShare: number;
          outstandingShare: number;
          settledShare: number;
        };
      };
    }
  > => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.doctorId) qs.set('doctorId', params.doctorId);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString();
    return get<RevenueDistributionSummary[]>(`/billing/revenue-distributions/summary${suffix ? `?${suffix}` : ''}`) as Promise<
      ApiResponse<RevenueDistributionSummary[]> & {
        meta: PaginationMeta;
        stats?: {
          totals: {
            totalAmount: number;
            doctorShare: number;
            hospitalShare: number;
            outstandingShare: number;
            settledShare: number;
          };
        };
      }
    >;
  },

  getTrashedRevenueDistributions: (
    page = 1,
    limit = 20,
    filters?: { search?: string },
  ): Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }> => {
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) qs.set('search', filters.search);
    return get<RevenueDistribution[]>(`/billing/revenue-distributions/trash?${qs.toString()}`) as Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }>;
  },

  trashRevenueDistributions: (ids: string[]): Promise<ApiResponse<{ trashed: string[]; skipped: string[]; notFound: string[] }>> => {
    return post<{ trashed: string[]; skipped: string[]; notFound: string[] }>('/billing/revenue-distributions/bulk-trash', { ids });
  },

  trashRevenueDistributionsByDoctors: (
    doctorIds: string[],
  ): Promise<ApiResponse<{ trashed: string[]; skipped: string[]; notFound: string[] }>> => {
    return post<{ trashed: string[]; skipped: string[]; notFound: string[] }>(
      '/billing/revenue-distributions/trash/by-doctors',
      { doctorIds },
    );
  },

  restoreRevenueDistributions: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/revenue-distributions/trash/bulk-restore', { ids });
  },

  permanentDeleteRevenueDistributions: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; skipped: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; skipped: string[]; notFound: string[] }>('/billing/revenue-distributions/trash/bulk-delete', { ids });
  },

  settleRevenueDistribution: (
    id: string,
    settledById?: string,
    options?: { method?: string; notes?: string },
  ): Promise<ApiResponse<RevenueDistribution>> => {
    return post<RevenueDistribution>(`/billing/revenue-distributions/${id}/settle`, {
      settledById,
      ...options,
    });
  },

  settleRevenueDistributionsBulk: (data: {
    ids?: string[];
    doctorId?: string;
    dateTo?: string;
    settledById?: string;
    method?: string;
    notes?: string;
  }): Promise<ApiResponse<{ settled: number; payments: any[] }>> => {
    return post<{ settled: number; payments: any[] }>('/billing/revenue-distributions/settle-bulk', data);
  },

  // Doctor payment ledger (payout vouchers)
  getDoctorPayments: (params?: {
    doctorId?: string;
    payeeType?: 'DOCTOR' | 'REFERRAL';
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<DoctorPayment[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }> => {
    const qs = new URLSearchParams();
    if (params?.doctorId) qs.set('doctorId', params.doctorId);
    if (params?.payeeType) qs.set('payeeType', params.payeeType);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<DoctorPayment[]>(`/billing/doctor-payments?${qs.toString()}`) as Promise<ApiResponse<DoctorPayment[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }>;
  },

  getDoctorPayment: (id: string): Promise<ApiResponse<DoctorPayment>> => {
    return get<DoctorPayment>(`/billing/doctor-payments/${id}`);
  },

  printDoctorPaymentVoucher: async (id: string): Promise<string> => {
    const response = await httpService.get(`/billing/doctor-payments/${id}/print`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  createLegacyDoctorPayment: (data: {
    doctorId: string;
    amount: number;
    paidAt: string;
    externalReference: string;
    method?: string;
    notes?: string;
  }): Promise<ApiResponse<DoctorPayment>> => {
    return post<DoctorPayment>('/billing/doctor-payments', data);
  },

  // ── Payee-agnostic payout facade (ADR-015) ──────────────────────────────
  // `payeeType` (DOCTOR | REFERRAL) is an attribute of the same ledger, not a
  // separate world. Replaces the doctor/referral split for the Payouts screen.

  /** Outstanding payables — one ledger of payees, optionally scoped by type. */
  getPayeeOutstanding: (params?: {
    payeeType?: PayeeType;
    payeeId?: string;
    status?: string;
    /** OPD (diagnostic receipts) or IPD (admissions); omit for both. */
    source?: PayoutSourceFilter;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<
    ApiResponse<PayeeOutstandingRow[]> & {
      meta: PaginationMeta;
      stats?: { totals: PayeeOutstandingTotals };
    }
  > => {
    const qs = new URLSearchParams();
    if (params?.payeeType) qs.set('payeeType', params.payeeType);
    if (params?.payeeId) qs.set('payeeId', params.payeeId);
    if (params?.status) qs.set('status', params.status);
    if (params?.source) qs.set('source', params.source);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<PayeeOutstandingRow[]>(`/billing/payouts/outstanding?${qs.toString()}`) as Promise<
      ApiResponse<PayeeOutstandingRow[]> & {
        meta: PaginationMeta;
        stats?: { totals: PayeeOutstandingTotals };
      }
    >;
  },

  /**
   * Accrual history — the completed receipts/consultations behind one payee's
   * balance ("where does this come from"). Newest first, paginated.
   */
  getPayeeAccruals: (params: {
    payeeType: PayeeType;
    payeeId: string;
    status?: string;
    /** OPD (diagnostic receipts) or IPD (admissions); omit for both. */
    source?: PayoutSourceFilter;
    /** Exclude settled accruals (ignored when `status` is given). */
    outstandingOnly?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.source) qs.set('source', params.source);
    if (params.outstandingOnly) qs.set('outstandingOnly', 'true');
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return get<RevenueDistribution[]>(
      `/billing/payouts/payee/${params.payeeType}/${params.payeeId}/accruals?${qs.toString()}`,
    ) as Promise<ApiResponse<RevenueDistribution[]> & { meta: PaginationMeta }>;
  },

  /** Payout vouchers — every issued voucher (`DP-` + `RP-`) in one ledger. */
  getPayoutVouchers: (params?: {
    payeeType?: PayeeType;
    payeeId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<DoctorPayment[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }> => {
    const qs = new URLSearchParams();
    if (params?.payeeType) qs.set('payeeType', params.payeeType);
    if (params?.payeeId) qs.set('payeeId', params.payeeId);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<DoctorPayment[]>(`/billing/payouts?${qs.toString()}`) as Promise<
      ApiResponse<DoctorPayment[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }
    >;
  },

  getPayoutVoucher: (id: string): Promise<ApiResponse<DoctorPayment>> => {
    return get<DoctorPayment>(`/billing/payouts/${id}`);
  },

  printPayoutVoucher: async (id: string): Promise<string> => {
    const response = await httpService.get(`/billing/payouts/${id}/print`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  /** Settle a payee (doctor or referral partner) into numbered vouchers. */
  settlePayouts: (data: {
    payeeType: PayeeType;
    payeeId: string;
    dateTo?: string;
    method?: string;
    notes?: string;
  }): Promise<ApiResponse<{ settled: number; payments: DoctorPayment[] }>> => {
    return post<{ settled: number; payments: DoctorPayment[] }>('/billing/payouts/settle', data);
  },

  /** Settle several payees in one request (one voucher per payee). */
  settlePayeesBulk: (data: {
    payees: Array<{ payeeType: PayeeType; payeeId: string }>;
    dateTo?: string;
    method?: string;
    notes?: string;
  }): Promise<ApiResponse<{ settled: number; payments: DoctorPayment[] }>> => {
    return post<{ settled: number; payments: DoctorPayment[] }>(
      '/billing/payouts/settle-bulk',
      data,
    );
  },

  /** Move a payee's outstanding accruals to the trash (reversible). */
  removeOutstandingByPayees: (data: {
    payees: Array<{ payeeType: PayeeType; payeeId: string }>;
  }): Promise<ApiResponse<{ trashed: string[]; skipped: string[]; notFound: string[] }>> => {
    return post<{ trashed: string[]; skipped: string[]; notFound: string[] }>(
      '/billing/payouts/outstanding/bulk-remove',
      data,
    );
  },

  /** Move issued payout vouchers to the trash, reversing their payouts. */
  trashPayoutVouchers: (
    ids: string[],
  ): Promise<
    ApiResponse<{ trashed: string[]; distributionsTrashed: string[]; notFound: string[] }>
  > => {
    return post<{ trashed: string[]; distributionsTrashed: string[]; notFound: string[] }>(
      '/billing/payouts/bulk-trash',
      { ids },
    );
  },
};

/** The payee of a payout. Doctor and referral partner share one ledger. */
export type PayeeType = 'DOCTOR' | 'REFERRAL';

export interface PayeeOutstandingTotals {
  totalAmount: number;
  share: number;
  outstandingShare: number;
  settledShare: number;
}

export interface PayeeOutstandingRow {
  payeeType: PayeeType;
  payeeId: string | null;
  payeeName: string;
  payeeCode: string;
  mobileNumber: string;
  totalAmount: number;
  share: number;
  pendingShare: number;
  postedShare: number;
  settledShare: number;
  outstandingShare: number;
}

/** OPD (diagnostic receipts) vs IPD (admissions) payout source filter. */
export type PayoutSourceFilter = 'OPD' | 'IPD';

export interface RevenueDistributionSummary {
  doctorId: string | null;
  doctorName: string;
  totalAmount: number;
  doctorShare: number;
  hospitalShare: number;
  pendingShare: number;
  postedShare: number;
  settledShare: number;
  outstandingShare: number;
}

export type RevenueDistributionSource = 'DIAGNOSTIC' | 'CONSULTATION' | 'ADMISSION';

export interface RevenueDistribution {
  id: string;
  diagnosticReceiptItemId?: string;
  receiptItem?: DiagnosticReceiptItem;
  sourceType?: RevenueDistributionSource;
  appointmentId?: string;
  appointment?: {
    id: string;
    patientName?: string;
    patient?: { id: string; firstName?: string; lastName?: string } | null;
    date?: string;
    appointmentType?: string;
  };
  /** Set when the accrual is the flat commission on an IPD admission. */
  admissionId?: string;
  admission?: {
    id: string;
    admittedAt?: string;
    patient?: { id: string; firstName?: string; lastName?: string } | null;
  } | null;
  doctorPaymentId?: string;
  /** The payout voucher this accrual was settled into, once settled. */
  doctorPayment?: { id: string; paymentNumber: string } | null;
  revenueRuleId?: string;
  revenueRule?: {
    id: string;
    name: string;
    ruleCode: string;
    doctorPercentage: number;
    hospitalPercentage: number;
  };
  totalAmount: number;
  doctorShare: number;
  hospitalShare: number;
  doctorPercentage: number;
  hospitalPercentage: number;
  doctorId?: string;
  doctor?: { id: string; name?: string; firstName?: string; lastName?: string };
  status: 'PENDING' | 'POSTED' | 'SETTLED';
  settledById?: string;
  settledAt?: string;
  deletedAt?: string;
  createdAt: string;
}

export interface DoctorPayment {
  id: string;
  paymentNumber: string;
  doctorId?: string;
  doctor?: {
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    employeeCode?: string;
  };
  /** Discriminates the payee: a doctor payout or a referral-partner payout. */
  payeeType?: 'DOCTOR' | 'REFERRAL';
  referralId?: string | null;
  /** Frozen payee name captured at payout time. */
  payeeNameSnapshot?: string;
  referral?: {
    id: string;
    name: string;
    code?: string;
  } | null;
  amount: number;
  status: 'PAID' | 'VOID';
  method?: string;
  notes?: string;
  paidAt: string;
  paidById?: string;
  paidBy?: { id: string; firstName?: string; lastName?: string };
  distributionCount: number;
  distributions?: RevenueDistribution[];
  externalReference?: string;
  isOpening?: boolean;
  createdAt: string;
}
